import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Tab,
  Tabs,
  FormGroup,
} from '@mui/material';
import { AppContext } from '../../core/Contexts/AppContext';
import { sendPublicInfo } from '../../core/services/send-publicInfo';
import { useNavigate } from 'react-router-dom';

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  },
  text: {
    fontSize: '18px',
    margin: '0 0 20px 0',
    lineHeight: '1.4',
  },
  button: {
    padding: '10px 20px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#007bff',
    color: '#fff',
    cursor: 'pointer',
  },
};

const PublicInfo = () => {
  const { setTitle, setHeaderType, setImage } = useContext(AppContext);
  const navigate = useNavigate();

  // Set header details
  useEffect(() => {
    setTitle('ელექტრონული განცხადების ფორმა');
    setImage('../../images/hero-images/pattern-bg-optimized.jpg');
    setHeaderType(1);
  }, [setTitle, setHeaderType, setImage]);

  // Manage tab state and submission status
  const [tabIndex, setTabIndex] = useState(0);
  const [status, setStatus] = useState('');

  // Form data including a file (if attached)
  const [formData, setFormData] = useState({
    applicationType: '',
    personalNumber: '',
    alternativePersonalNumber: '',
    note: '',
    documentType: '',
    viaEmail: false,
    agreeContract: false,
    receiveUpdates: false,
    acceptTerms: false,
    publisher: '',
    idNumber: '',
    name: '',
    phoneNumber: '',
    note2: '',
    viaEmail2: false,
    documentType2: '',
    agreeContract2: false,
    receiveUpdates2: false,
    acceptTerms2: false,
    file: null,
  });

  // Local state to display selected file name
  const [selectedFile, setSelectedFile] = useState(null);

  // Handlers for tab change and form fields
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  // Update both selectedFile (for display) and formData.file for submission
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setFormData((prev) => ({ ...prev, file }));
  };

  // Submit handler calls sendPublicInfo with all data
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await sendPublicInfo(formData);
      if (response.status === 200) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error sending public info:', error);
      setStatus('error');
    }
  };

  const handleModalClose = () => {
    setStatus('');
    navigate('/');
  };

  return (
    <Container maxWidth='lg'>
      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label='ფიზიკური პირი' />
        <Tab label='იურიდიული პირი' />
        <Tab label='სკანირებული ფაილის გაგზავნა' />
      </Tabs>

      {tabIndex === 0 && (
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
        >
          <TextField
            label='განმცხადებელი'
            name='applicationType'
            value={formData.applicationType}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='პირადი ნომერი'
            name='personalNumber'
            value={formData.personalNumber}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='საკონტაქტო ტელეფონის ნომერი'
            name='alternativePersonalNumber'
            value={formData.alternativePersonalNumber}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='მოთხოვნა'
            name='note'
            value={formData.note}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            required
          />
          <Typography variant='h6'>
            რა ფორმით მსურს ინფორმაციის მიღება?
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.viaEmail}
                  onChange={handleCheckboxChange}
                  name='viaEmail'
                />
              }
              label='ელექტრონული ფოსტის საშუალებით (გთხოვთ, მიუთითოთ იმ ელექტრონული ფოსტის მისამართი, რომელზეც გსურთ ინფორმაციის მიღება)'
            />
          </FormGroup>
          <TextField
            label='ელ-ფოსტა'
            name='documentType'
            value={formData.documentType}
            onChange={handleChange}
            fullWidth
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeContract}
                  onChange={handleCheckboxChange}
                  name='agreeContract'
                />
              }
              label='კომპაქტურ დისკზე ჩაწერილი (გთხოვთ, მიუთითოთ მისამართი)'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.receiveUpdates}
                  onChange={handleCheckboxChange}
                  name='receiveUpdates'
                />
              }
              label='მატერიალური სახით (გთხოვთ, მიუთითოთ მისამართი)'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.acceptTerms}
                  onChange={handleCheckboxChange}
                  name='acceptTerms'
                />
              }
              label='დედანში გაცნობის გზით (გთხოვთ, მიუთითოთ მისამართი)'
            />
          </FormGroup>
          <Button type='submit' variant='contained' color='primary'>
            გაგზავნა
          </Button>
        </Box>
      )}

      {tabIndex === 1 && (
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
        >
          <TextField
            label='განმცხადებელი (ინფორმაციის მომთხოვნი პირის დასახელება)'
            name='publisher'
            value={formData.publisher}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='საიდენტიფიკაციო ნომერი'
            name='idNumber'
            value={formData.idNumber}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='ხელმძღვანელი პირის სახელი და გვარი'
            name='name'
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='საკონტაქტო ტელეფონის ნომერი'
            name='phoneNumber'
            value={formData.phoneNumber}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label='გაფრთხილება'
            name='note2'
            value={formData.note2}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            required
          />
          <Typography variant='h6'>
            რა ფორმით გსურთ ინფორმაციის მიღება?
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.viaEmail2}
                  onChange={handleCheckboxChange}
                  name='viaEmail2'
                />
              }
              label='ელექტრონული ფოსტის საშუალებით (გთხოვთ, მიუთითოთ იმ ელექტრონული ფოსტის მისამართი, რომელზეც გსურთ ინფორმაციის მიღება)'
            />
          </FormGroup>
          <TextField
            label='ელ-ფოსტა'
            name='documentType2'
            value={formData.documentType2}
            onChange={handleChange}
            fullWidth
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeContract2}
                  onChange={handleCheckboxChange}
                  name='agreeContract2'
                />
              }
              label='კომერციული დაარსების ჩარიცხვა'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.receiveUpdates2}
                  onChange={handleCheckboxChange}
                  name='receiveUpdates2'
                />
              }
              label='მიღებული სუბსიდიების შესახებ ინფორმაციის მიღება'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.acceptTerms2}
                  onChange={handleCheckboxChange}
                  name='acceptTerms2'
                />
              }
              label='სხვა დაკავშირებული ინფორმაციის მიღება'
            />
          </FormGroup>
          <Button type='submit' variant='contained' color='primary'>
            გაგზავნა
          </Button>
        </Box>
      )}

      {tabIndex === 2 && (
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
        >
          <Typography variant='body1' sx={{ mb: 2 }}>
            ელექტრონული ფორმით წარდგენილი მოთხოვნა საჯარო ინფორმაციის გაცემაზე
            უნდა შეიცავდეს:
            <br />
            ა) ინფორმაციის მომკითხვნი პირის დასახელება, საიდენტიფიკაციო ნომერი,
            იურიდიული მისამართი, ხელმძღვანელი პირის სახელი, გვარი; ფიზიკური
            პირის შემთხვევაში – ინფორმაცია პირად ნომრის შესახებ;
            <br />
            ბ) მითითებას საჯარო ინფორმაციის მიღების სასურველი ფორმის შესახებ –
            ელექტრონული ფოსტის საშუალებით, კომპაქტურ დისკზე ჩაწერილი,
            მატერიალური სახით თუ დედანში გაცნობის გზით.
            <br />
            <br />
            შენიშვნა* (pdf, docx, jpg, jpeg, png), ფაილის ზომა არ უნდა
            აღემატებოდეს 2mb-ს.
          </Typography>

          <div className='file-input-container'>
            <input
              id='file-upload'
              type='file'
              accept='.pdf,.docx,.jpg,.jpeg,.png'
              onChange={handleFileChange}
            />
            <label htmlFor='file-upload' className='file-upload-label'>
              დოკუმენტის ატვირთვა
            </label>
            <span className='file-chosen'>
              {selectedFile ? selectedFile.name : 'დასახელება...'}
            </span>
          </div>

          <Button type='submit' variant='contained' color='primary'>
            გაგზავნა
          </Button>
        </Box>
      )}

      {/* Modal Popup */}
      {status === 'success' && (
        <Box sx={modalStyles.overlay}>
          <Box sx={modalStyles.modal}>
            <Typography sx={modalStyles.text}>
              თქვენი შეტყობინება გაიგზავნა წარმატებით
            </Typography>
            <Button onClick={handleModalClose} sx={modalStyles.button}>
              დახურვა
            </Button>
          </Box>
        </Box>
      )}
      {status === 'error' && (
        <Box sx={modalStyles.overlay}>
          <Box sx={modalStyles.modal}>
            <Typography sx={modalStyles.text}>
              შეცდომა! შეტყობინების გაგზავნა ვერ მოხერხდა.
            </Typography>
            <Button onClick={() => setStatus('')} sx={modalStyles.button}>
              დახურვა
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default PublicInfo;
